import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Select from "react-select"
import DatePicker from "react-datepicker"
import moment from "moment"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"
import { DEFAULT_SEASON_YEAR } from "utils/seasonYear"

const service = new UserService()

/** Max Position options for Regional Report (only these map to API groupBy) */
const MAX_POSITION_OPTIONS = [
  "Manager",
  "Senior Manager",
  "Regional",
  "Senior Regional",
  "Partner",
]

/** Map Max Position to regional API groupBy (backend GROUPING_MAP) */
const MAX_POSITION_TO_GROUP_BY: Record<string, string> = {
  Manager: "MGMT",
  "Senior Manager": "Sr MGMT",
  Regional: "Region",
  "Senior Regional": "Sr Region",
  Partner: "Partner",
}

/** All 5 options show the leader dropdown (optional filter; empty = all) */
const MAX_POSITION_HAS_LEADERS = [...MAX_POSITION_OPTIONS]

/** API row shape from regional-reporting (signed_badge_ids is for debugging, not shown in table) */
type RegionalRow = {
  _id: string
  leader_name: string
  total_ads: number
  total_candidates: number
  contacted_candidates: number
  signed_candidates: number
  ad_amount_total: number
  outreach_speed_count: number
  median_outreach_speed_hours: number | null
  rws_total?: number
  fp_total?: number
  signed_badge_ids?: string[]
}

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
  formatValue,
}: {
  value: number
  maxVal: number
  colorClass?: string
  formatValue?: (v: number) => string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  const display = formatValue ? formatValue(value) : String(value)
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <span className="text-sm tabular-nums shrink-0 text-ink w-12 text-right">{display}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export const RegionalReport = () => {
  const [maxPosition, setMaxPosition] = useState<string>("Manager")
  const [leaderIds, setLeaderIds] = useState<string[]>([])
  const [date, setDate] = useState<string>("YEARTODATE")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [year] = useState<string>(DEFAULT_SEASON_YEAR)
  const [leadersFromServer, setLeadersFromServer] = useState<{ id: string; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [apiData, setApiData] = useState<Record<string, unknown> | null>(null)

  const groupBy = maxPosition ? (MAX_POSITION_TO_GROUP_BY[maxPosition] ?? "") : ""

  const leaderOptions = useMemo(
    () =>
      leadersFromServer.map((l) => ({
        value: l.id,
        label: `${l.name} (${l.id})`,
      })),
    [leadersFromServer]
  )
  const selectedLeaders = useMemo(
    () => leaderOptions.filter((o) => leaderIds.includes(o.value)),
    [leaderOptions, leaderIds]
  )

  const queryParams = useMemo(
    () => ({
      groupBy: groupBy || undefined,
      leaderId: leaderIds.length > 0 ? leaderIds : undefined,
      date: date || undefined,
    }),
    [groupBy, leaderIds, date]
  )

  const fetchReport = useCallback(() => {
    if (!groupBy) return
    setLoading(true)
    service
      .getRegionalReporting(queryParams)
      .then((resp: any) => {
        if (resp?.data?.success && resp?.data?.data) setApiData(resp.data.data)
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false))
  }, [queryParams, groupBy])

  const initialFetchDone = useRef(false)
  useEffect(() => {
    if (!initialFetchDone.current && groupBy) {
      initialFetchDone.current = true
      fetchReport()
    }
  }, [groupBy, fetchReport])

  const fetchLeadersForDropdown = useCallback(() => {
    if (!maxPosition || !MAX_POSITION_HAS_LEADERS.includes(maxPosition)) return
    service
      .getSignedRepsLeaders(year, maxPosition)
      .then((resp: any) => {
        if (resp?.data?.success && Array.isArray(resp.data.data)) {
          setLeadersFromServer(resp.data.data)
        } else {
          setLeadersFromServer([])
        }
      })
      .catch(() => setLeadersFromServer([]))
  }, [year, maxPosition])

  const showLeaderDropdown = maxPosition && MAX_POSITION_HAS_LEADERS.includes(maxPosition)

  const { tableRows, dataGroupBy } = useMemo(() => {
    if (!apiData) return { tableRows: [], dataGroupBy: null }
    const keyForCurrent = groupBy ? (`by_${groupBy}` as keyof typeof apiData) : null
    const arr = keyForCurrent ? (apiData[keyForCurrent] as RegionalRow[] | undefined) : undefined
    if (Array.isArray(arr) && arr.length > 0) {
      return { tableRows: arr, dataGroupBy: groupBy }
    }
    const firstKey = Object.keys(apiData)[0] as keyof typeof apiData | undefined
    const fallback = firstKey && Array.isArray(apiData[firstKey]) ? (apiData[firstKey] as RegionalRow[]) : []
    const fallbackGroupBy = firstKey ? firstKey.replace(/^by_/, '') : null
    return { tableRows: fallback, dataGroupBy: fallbackGroupBy }
  }, [apiData, groupBy])

  const maxAds = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.total_ads)) : 1), [tableRows])
  const maxSpend = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.ad_amount_total)) : 1), [tableRows])
  const maxApplicants = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.total_candidates)) : 1), [tableRows])
  const maxContacted = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.contacted_candidates)) : 1), [tableRows])
  const maxMedHours = useMemo(
    () =>
      tableRows.length
        ? Math.max(...tableRows.map((r) => (r.median_outreach_speed_hours != null ? r.median_outreach_speed_hours : 0)))
        : 1,
    [tableRows]
  )
  const maxSigned = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.signed_candidates)) : 1), [tableRows])
  const maxRws = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.rws_total ?? 0)) : 1),
    [tableRows]
  )
  const maxFp = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.fp_total ?? 0)) : 1),
    [tableRows]
  )

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Regional Report</h1>
        <p className="page-subtitle">Social media ads regional reporting.</p>
      </div>

      {/* Filters — same as Signed Reps with API-driven options */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Max Position</label>
            <select
              value={maxPosition}
              onChange={(e) => {
                const v = e.target.value
                setMaxPosition(v)
                setLeaderIds([])
                setLeadersFromServer([])
              }}
              className="px-3 py-2 text-sm"
            >
              <option value="">Max Position</option>
              {MAX_POSITION_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {showLeaderDropdown && (
            <div className="flex flex-col gap-1 min-w-[280px]">
              <label className="field-label">Select by {maxPosition} (optional; leave empty for all)</label>
              <Select
                isMulti
                isSearchable
                placeholder="Select users..."
                value={selectedLeaders}
                options={leaderOptions}
                onChange={(selected) => setLeaderIds((selected || []).map((s) => s.value))}
                onMenuOpen={fetchLeadersForDropdown}
                className="react-select min-w-[260px]"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                  menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                    color: "#111827",
                  }),
                  singleValue: (base) => ({ ...base, color: "#111827" }),
                  multiValue: (base) => ({ ...base, backgroundColor: "#f1f3f5" }),
                  multiValueLabel: (base) => ({ ...base, color: "#111827" }),
                  input: (base) => ({ ...base, color: "#111827" }),
                }}
              />
            </div>
          )}

          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Signed Rep Date</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") {
                  setDate(e.target.value)
                } else {
                  setShowCustom(true)
                }
              }}
              className="px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-60 rounded-lg border border-line bg-white shadow-card-lg text-ink px-3 text-sm"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-60 py-2 px-3 text-sm"
                  />
                }
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!groupBy || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {/* Table: when Max Position changes, keep showing last data until Search is clicked */}
      {dataGroupBy && groupBy && dataGroupBy !== groupBy && tableRows.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-amber-800 text-sm">
          Showing data for <strong>{dataGroupBy}</strong>. Click Search to load data for <strong>{groupBy}</strong>.
        </div>
      )}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Regional Stats Slaved to Creation Date</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Ads</th>
                <th className="px-4 py-3 font-medium">Spend</th>
                <th className="px-4 py-3 font-medium">Applicants</th>
                <th className="px-4 py-3 font-medium">Contacted</th>
                <th className="px-4 py-3 font-medium">MED Hours to Outreach</th>
                <th className="px-4 py-3 font-medium">Signed</th>
                <th className="px-4 py-3 font-medium">RWS</th>
                <th className="px-4 py-3 font-medium">FP</th>
              </tr>
            </thead>
            <tbody>
              {loading && !apiData ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-ink-muted">
                    {apiData ? "No data for the selected filters." : "Select filters and click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row) => (
                  <tr key={row._id} className="border-b border-line hover:bg-surface transition-colors">
                    <td className="px-4 py-2.5 text-ink text-sm font-medium">{row.leader_name}</td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.total_ads} maxVal={maxAds} colorClass="bg-blue-500" />
                    </td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.ad_amount_total} maxVal={maxSpend} colorClass="bg-orange-500" formatValue={(v) => `$${v.toLocaleString()}`} />
                    </td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.total_candidates} maxVal={maxApplicants} colorClass="bg-purple-500" />
                    </td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.contacted_candidates} maxVal={maxContacted} colorClass="bg-green-400" />
                    </td>
                    <td className="px-4 py-2.5">
                      {row.median_outreach_speed_hours == null ? (
                        <span className="text-ink-secondary text-sm">—</span>
                      ) : (
                        <BarCell value={row.median_outreach_speed_hours} maxVal={maxMedHours} colorClass="bg-blue-400" formatValue={(v) => Number(v).toFixed(2)} />
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.signed_candidates} maxVal={maxSigned} colorClass="bg-yellow-500" />
                    </td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.rws_total ?? 0} maxVal={maxRws} colorClass="bg-pink-500" />
                    </td>
                    <td className="px-4 py-2.5">
                      <BarCell value={row.fp_total ?? 0} maxVal={maxFp} colorClass="bg-indigo-500" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                <td className="px-4 py-2.5">Grand total</td>
                <td className="px-4 py-2.5">{tableRows.reduce((s, r) => s + r.total_ads, 0)}</td>
                <td className="px-4 py-2.5">${tableRows.reduce((s, r) => s + r.ad_amount_total, 0).toLocaleString()}</td>
                <td className="px-4 py-2.5">{tableRows.reduce((s, r) => s + r.total_candidates, 0)}</td>
                <td className="px-4 py-2.5">{tableRows.reduce((s, r) => s + r.contacted_candidates, 0)}</td>
                <td className="px-4 py-2.5">—</td>
                <td className="px-4 py-2.5">{tableRows.reduce((s, r) => s + r.signed_candidates, 0)}</td>
                <td className="px-4 py-2.5">{tableRows.reduce((s, r) => s + (r.rws_total ?? 0), 0)}</td>
                <td className="px-4 py-2.5">{tableRows.reduce((s, r) => s + (r.fp_total ?? 0), 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
